/**
 * WorldSettingsCockpit.js
 * Dashboard as the first screen in World Settings.
 * Shows SalesGod potential (1–100%) with weighted progress bar and smart next steps.
 * Weights: basics ~70%, recommended ~20%, optional ~10%.
 * Extend via COCKPIT_CHECKS – add new items there only.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { collection, getDocs, query as fsQuery, limit as fsLimit } from 'firebase/firestore';
import { database, firestore } from '../firebaseConfig';
import { useActiveWorld } from '../hooks/useActiveWorld';
import useCreditBalance from '../hooks/useCreditBalance';
import { CREDIT_PLANS, ROLLOVER_RATIO, resolveMembershipPlanId } from '../config/creditPlans';
import {
  daysUntil,
  formatCreditCycleDate,
  resolveNextCreditCycle,
  wasRefilledThisCycle,
} from '../utils/creditCycle';
import { FEATURE_CATALOG } from './cockpitFeatureCatalog';
import CockpitDataVault from './CockpitDataVault';
import UltimateSetupModal from './UltimateSetup/UltimateSetupModal';
import UpgradeAccountModal from './UpgradeAccountModal';
import { FOUNDATION_ORDER, deriveUltimateFoundationStatus } from '../utils/ultimateSetupDiagnostics';
import './WorldSettingsCockpit.css';

/** Load Academy categories + videos for feature card headers */
function useAcademyMedia() {
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [catSnap, vidSnap] = await Promise.all([
          get(ref(database, 'Academy/categories')),
          get(ref(database, 'Academy/videos')),
        ]);
        if (cancelled) return;
        const cats = catSnap.exists() ? Object.entries(catSnap.val()).map(([id, c]) => ({ ...c, id })) : [];
        const vids = vidSnap.exists() ? Object.entries(vidSnap.val()).map(([id, v]) => ({ ...v, id })) : [];
        console.log('[Cockpit] Academy categories:', cats.map((c) => ({ id: c.id, name: c.name, imageUrl: c.imageUrl || '✗' })));
        console.log('[Cockpit] Academy videos:', vids.map((v) => ({ id: v.id, title: v.title, catId: v.categoryId, thumb: v.thumbnailUrl ? '✓' : '✗', videoUrl: v.videoUrl || '—' })));
        setCategories(cats);
        setVideos(vids);
      } catch (e) {
        if (!cancelled) console.warn('[Cockpit] Academy load error', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);
  return { categories, videos };
}

/**
 * Keyword map: feature id → terms to match against Academy VIDEO titles.
 * Matching is case-insensitive, partial (includes). Both German and English terms.
 */
const FEATURE_VIDEO_KEYWORDS = {
  emailAccount: ['email account', 'email konto', 'e-mail konto', 'postfach', 'imap', 'smtp', 'email connect', 'email einrichten', 'email anbinden', 'hier geht', 'los geht', 'getting started', 'start'],
  aiSettings: ['ai setting', 'ai einstellung', 'ki einstellung', 'ai config', 'modell', 'model', 'sprache', 'language', 'bereinigung', 'cleanup'],
  footer: ['footer', 'signatur', 'fußzeile', 'signature'],
  topics: ['template', 'topic', 'vorlage', 'thema', 'themen', 'email template', 'e-mail vorlage', 'anwendungsfall', 'use case'],
  agents: ['agent', 'reply agent', 'antwort agent', 'tag', 'auto reply', 'auto-antwort', 'automatische antwort'],
  warmup: ['warmup', 'warm-up', 'warm up', 'aufwärm', 'aufwaerm', 'zustellbarkeit', 'deliverability', 'reputation'],
  safeGuard: ['safeguard', 'safe guard', 'compliance', 'sicherheit', 'prüfung', 'risiko', 'check'],
  blacklist: ['blacklist', 'black list', 'blocklist', 'sperrliste', 'block'],
  leadFlows: ['lead flow', 'flow', 'automation', 'follow-up', 'followup', 'automatisierung', 'sequenz', 'nachfassen'],
  research: ['research', 'spy', 'lead intel', 'intelligence', 'recherche', 'anreicher', 'entscheider', 'decision maker'],
  integrations: ['api', 'integration', 'webhook', 'zapier', 'schnittstelle', 'anbindung'],
  customFields: ['custom field', 'felder', 'eigene felder', 'benutzerdefiniert', 'personalisierung'],
  extraSettings: ['calendly', 'booking', 'termin', 'buchung'],
  whatsapp: ['whatsapp'],
  whisper: ['instagram', 'insta', 'dm', 'whisper'],
  team: ['team', 'member', 'mitglied', 'nutzer'],
};

/** Secondary keyword map for broader fuzzy matching on video titles */
const FEATURE_FUZZY_TERMS = {
  emailAccount: ['email', 'konto', 'account', 'postfach', 'connect', 'anbind', 'einricht'],
  aiSettings: ['ai', 'ki', 'model', 'sprach', 'bereinig', 'clean'],
  footer: ['footer', 'signatur', 'fußzeile'],
  topics: ['template', 'topic', 'vorlag', 'thema', 'anwendung'],
  agents: ['agent', 'antwort', 'reply', 'tag'],
  warmup: ['warmup', 'warm', 'aufwärm', 'zustellbar', 'deliverabil'],
  safeGuard: ['safeguard', 'safe', 'guard', 'compli', 'sicher', 'prüf', 'risiko'],
  blacklist: ['blacklist', 'blocklist', 'sperr'],
  leadFlows: ['flow', 'automat', 'follow', 'sequenz', 'nachfass'],
  research: ['research', 'spy', 'intel', 'recherch', 'anreicher', 'entscheid'],
  integrations: ['api', 'integrat', 'webhook', 'zapier'],
  customFields: ['custom', 'feld', 'field', 'personal'],
  extraSettings: ['calendly', 'booking', 'termin', 'buchung'],
  whatsapp: ['whatsapp'],
  whisper: ['instagram', 'insta', 'whisper'],
  team: ['team', 'mitglied', 'member'],
};

/** Also match against target/lead-related videos for lead-centric features */
const LEAD_FEATURES = new Set(['leadFlows', 'research', 'customFields', 'blacklist']);

function extractYouTubeThumbnail(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

function getVideoThumbnail(video) {
  if (!video) return null;
  if (video.thumbnailUrl) return video.thumbnailUrl;
  if (video.videoUrl) return extractYouTubeThumbnail(video.videoUrl);
  return null;
}

/**
 * Match a feature to a specific Academy video by scanning video titles + descriptions.
 * Returns { video, thumbnail, categoryId } or null.
 */
function matchFeatureToVideo(featureId, featureTitle, videos) {
  if (!videos.length) return null;

  const keywords = FEATURE_VIDEO_KEYWORDS[featureId] || [];
  const fuzzy = FEATURE_FUZZY_TERMS[featureId] || [];

  // Pass 1: exact keyword phrase match in video title or description
  for (const video of videos) {
    const text = `${video.title || ''} ${video.description || ''}`.toLowerCase();
    if (keywords.some((kw) => text.includes(kw))) {
      const thumb = getVideoThumbnail(video);
      if (thumb) return { video, thumbnail: thumb, categoryId: video.categoryId };
    }
  }

  // Pass 2: fuzzy stem matching – count how many fuzzy terms hit
  let bestVideo = null;
  let bestScore = 0;
  for (const video of videos) {
    const text = `${video.title || ''} ${video.description || ''}`.toLowerCase();
    const score = fuzzy.reduce((s, t) => s + (text.includes(t) ? 1 : 0), 0);

    // Bonus: if video title contains words from feature title
    const featureWords = (featureTitle || '').toLowerCase().split(/[\s\-_()&]+/).filter((w) => w.length > 2);
    const titleBonus = featureWords.reduce((s, w) => s + (text.includes(w) ? 0.5 : 0), 0);

    // Bonus for lead-related features matching "target"/"lead"/"liste"/"daten" videos
    let leadBonus = 0;
    if (LEAD_FEATURES.has(featureId) && /target|lead|liste|daten|kontakt|import/.test(text)) leadBonus = 0.5;

    const total = score + titleBonus + leadBonus;
    if (total > bestScore) { bestScore = total; bestVideo = video; }
  }

  if (bestVideo && bestScore >= 1) {
    const thumb = getVideoThumbnail(bestVideo);
    if (thumb) return { video: bestVideo, thumbnail: thumb, categoryId: bestVideo.categoryId };
  }

  return null;
}

/**
 * Get header image + category id for a feature card.
 * Tries: 1) explicit academyCategoryId, 2) video title matching, 3) category image fallback.
 */
function getFeatureMedia(featureId, featureTitle, academyCategoryId, categories, videos) {
  // If explicit category ID set in catalog, use category image
  if (academyCategoryId) {
    const cat = categories.find((c) => c.id === academyCategoryId);
    if (cat) {
      const catId = cat.id;
      if (cat.imageUrl) return { image: cat.imageUrl, categoryId: catId };
      const first = videos.filter((v) => v.categoryId === catId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
      const thumb = getVideoThumbnail(first);
      return { image: thumb, categoryId: catId };
    }
  }

  // Match by video title/description keywords
  const match = matchFeatureToVideo(featureId, featureTitle, videos);
  if (match) return { image: match.thumbnail, categoryId: match.categoryId };

  return { image: null, categoryId: null };
}

const EMAIL_ACCOUNT_LIMITS = { standard: 5, premium: 15, unlimited: 50 };

/**
 * Above this potential the setup is essentially done, so the hero shrinks to a
 * single quiet row and lets the cards below it carry the attention.
 */
const HERO_COMPACT_MIN_PERCENT = 80;

const hasKeys = (obj) => obj && typeof obj === 'object' && Object.keys(obj).length > 0;
const objectLength = (obj) => (obj && typeof obj === 'object' ? Object.keys(obj).length : 0);

/**
 * Data-driven checklist. Add new entries here.
 * icon: Font Awesome class (fa fa-*)
 */
const COCKPIT_CHECKS = [
  { id: 'emailAccount', weight: 20, tab: 'email', icon: 'fa fa-envelope', text: 'Connect at least one email account', check: (d) => d.emailAccountsCount >= 1 },
  { id: 'emailSettings', weight: 15, tab: 'email', icon: 'fa fa-cog', text: 'Set up email accounts (IMAP/SMTP)', check: (d, ctx) => (d.emailAccountsCount >= 1) || !!(d.emailSettings && (d.emailSettings.imap_host || d.emailSettings.smtp_host) && (d.emailSettings.email_username || (d.emailSettings.email && String(d.emailSettings.email).trim()))) },
  { id: 'footer', weight: 10, tab: 'emailPreferences', icon: 'fa fa-align-left', text: 'Customize email footer', check: (d) => d.footerSettings && ((d.footerSettings.footer_text && String(d.footerSettings.footer_text).trim()) || (d.footerSettings.useMultipleFooters && Array.isArray(d.footerSettings.footerList) && d.footerSettings.footerList.length > 0)) },
  { id: 'topics', weight: 10, tab: 'topics', icon: 'fa fa-file-alt', text: 'Create at least one email template', check: (d) => (objectLength(d.selectedTopics) + objectLength(d.customTopics)) >= 1 },
  { id: 'agents', weight: 5, tab: 'agents', icon: 'fa fa-brain', text: 'Define your Inbox Reply cases', check: (d) => objectLength(d.agents) >= 1 },
  { id: 'warmup', weight: 5, tab: 'trueWarmup', icon: 'fa fa-fire', text: 'Configure Warmup Engine (for better deliverability)', check: (d) => d.warmupAccountsCount >= 1 },
  { id: 'safeGuard', weight: 5, tab: 'safeGuard', icon: 'fa fa-shield-alt', text: 'Enable SafeGuard for safe emails', check: (d) => hasKeys(d.safeGuardSettings) },
  { id: 'leadFlows', weight: 5, tab: 'leadSettings', icon: 'fa fa-project-diagram', text: 'Set up Lead Flows for automation', check: (d) => hasKeys(d.leadFlows) },
  { id: 'multipleAccounts', weight: 5, tab: 'email', icon: 'fa fa-envelope-open-text', text: (d, ctx) => `${ctx.accountLimit - d.emailAccountsCount} more email account(s) available (${d.emailAccountsCount}/${ctx.accountLimit})`, check: (d, ctx) => d.emailAccountsCount >= 2 || ctx.accountLimit <= 1 },
  { id: 'leadsImport', weight: 10, tab: 'worlddb', icon: 'fa fa-upload', text: 'Import your first leads list', check: (d) => !!d.hasLeads },
  { id: 'cleaner', weight: 10, tab: 'ai', icon: 'fa fa-broom', text: 'Set up the AI Cleaner guidelines', check: (d) => hasKeys(d.cleanerGuidelines) || !!(d.aiSettings?.cleanupGuidelines && String(d.aiSettings.cleanupGuidelines).trim()) },
  { id: 'integrations', weight: 5, tab: 'api', icon: 'fa fa-plug', text: 'Use API, Calendly or Custom Fields', check: (d) => (d.moreSettings && (d.moreSettings.calendlyBookingLink || objectLength(d.moreSettings) > 0)) || hasKeys(d.apiMappings) || hasKeys(d.customFields) || hasKeys(d.whatsappSettings) },
];

function useCockpitData(userId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(true);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const [
          emailSnap,
          aiSnap,
          footerSnap,
          selectedTopicsSnap,
          customTopicsSnap,
          agentsSnap,
          safeGuardSnap,
          leadFlowsSnap,
          moreSnap,
          userSnap,
          apiSnap,
          cfSnap,
          waSnap,
          emailHealthSnap,
          cleanerSnap,
          creditPlanSnap,
          vaultEntriesSnap,
        ] = await Promise.all([
          get(ref(database, `World/${userId}/settings/email`)),
          get(ref(database, `World/${userId}/settings/ai`)),
          get(ref(database, `World/${userId}/settings/footer`)),
          get(ref(database, `World/${userId}/settings/selectedTopics`)),
          get(ref(database, `World/${userId}/settings/customTopics`)),
          get(ref(database, `World/${userId}/settings/agents`)),
          get(ref(database, `World/${userId}/settings/safeGuardSettings`)),
          get(ref(database, `World/${userId}/settings/leadFlows`)),
          get(ref(database, `World/${userId}/settings/moreSettings`)),
          get(ref(database, `Users/${userId}`)),
          get(ref(database, `World/${userId}/settings/apiMappings`)),
          get(ref(database, `World/${userId}/settings/customFields`)),
          get(ref(database, `World/${userId}/settings/whatsapp`)),
          get(ref(database, `World/${userId}/emailHealth`)),
          get(ref(database, `World/${userId}/settings/cleanupGuidelinesList`)),
          get(ref(database, `World/${userId}/creditPlan`)),
          get(ref(database, `World/${userId}/dataVault/entries`)),
        ]);

        if (cancelled) return;

        // Sub-instances share the parent owner's credit plan / refill cycle.
        // Always prefer the parent's plan when this world is a sub — a stale
        // child creditPlan (or one without nextDistribution) would otherwise
        // hide the cycle date. Mirrors deductCredits.js → resolveCreditOwnerUid.
        const tempUserData = userSnap.exists() ? userSnap.val() : {};
        let creditPlanResolved = creditPlanSnap.exists() ? creditPlanSnap.val() : null;
        const parentUid = tempUserData.parentOwnerUid;
        if (parentUid && typeof parentUid === 'string' && parentUid !== userId) {
          try {
            const parentPlanSnap = await get(ref(database, `World/${parentUid}/creditPlan`));
            if (parentPlanSnap.exists()) creditPlanResolved = parentPlanSnap.val();
          } catch (_) {}
        }

        let emailAccountsCount = 0;
        let warmupAccountsCount = 0;
        let hasLeads = false;
        let hasBlacklist = false;
        try {
          const accCol = collection(firestore, 'world_users', userId, 'emailAccounts');
          const accSnap = await getDocs(accCol);
          emailAccountsCount = accSnap.docs.length;
          // New warmup logic: warmup lives per-mailbox on the email account doc
          // (Mailivery / legacy WarmUpInbox), not in settings/warmupFlows anymore.
          warmupAccountsCount = accSnap.docs.filter((doc) => {
            const a = doc.data() || {};
            const status = String(a.mailiveryStatus || '').toLowerCase();
            if (a.warmupProvider === 'mailivery') return status !== 'paused' && status !== 'deleted';
            if (a.warmupProvider === 'warmupinbox') return !!a.warmupInboxId;
            return false;
          }).length;
        } catch (_) {}
        try {
          const leadsCol = collection(firestore, `World/${userId}/db`);
          const leadsSnap = await getDocs(fsQuery(leadsCol, fsLimit(1)));
          hasLeads = leadsSnap.docs.length > 0;
        } catch (_) {}
        try {
          // Blacklist entries live in the Firestore collection World/{uid}/blacklist.
          const blacklistCol = collection(firestore, `World/${userId}/blacklist`);
          const blacklistSnap = await getDocs(fsQuery(blacklistCol, fsLimit(1)));
          hasBlacklist = blacklistSnap.docs.length > 0;
        } catch (_) {}

        const userData = userSnap.exists() ? userSnap.val() : {};
        const emailPlan = userData.emailPlan || 'standard';
        const membershipPlan = userData.membershipPlan || null;
        const hasAcademy = userData.hasAcademy === true;

        let vaultEntryCount = 0;
        if (vaultEntriesSnap.exists()) {
          const raw = vaultEntriesSnap.val();
          vaultEntryCount = raw && typeof raw === 'object' ? Object.keys(raw).length : 0;
        }

        setData({
          emailAccountsCount,
          warmupAccountsCount,
          emailPlan,
          membershipPlan,
          hasAcademy: !!hasAcademy,
          accountCreatedAt: userData.signupTimestamp || userData.setupDate || null,
          emailSettings: emailSnap.exists() ? emailSnap.val() : null,
          aiSettings: aiSnap.exists() ? aiSnap.val() : null,
          footerSettings: footerSnap.exists() ? footerSnap.val() : null,
          selectedTopics: selectedTopicsSnap.exists() ? selectedTopicsSnap.val() : null,
          customTopics: customTopicsSnap.exists() ? customTopicsSnap.val() : null,
          agents: agentsSnap.exists() ? agentsSnap.val() : null,
          safeGuardSettings: safeGuardSnap.exists() ? safeGuardSnap.val() : null,
          leadFlows: leadFlowsSnap.exists() ? leadFlowsSnap.val() : null,
          moreSettings: moreSnap.exists() ? moreSnap.val() : null,
          apiMappings: apiSnap.exists() ? apiSnap.val() : null,
          customFields: cfSnap.exists() ? cfSnap.val() : null,
          whatsappSettings: waSnap.exists() ? waSnap.val() : null,
          emailHealth: emailHealthSnap.exists() ? emailHealthSnap.val() : null,
          cleanerGuidelines: cleanerSnap.exists() ? cleanerSnap.val() : null,
          creditPlan: creditPlanResolved,
          hasLeads,
          hasBlacklist,
          vaultEntryCount,
        });
      } catch (e) {
        if (!cancelled) console.warn('[Cockpit] load error', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  return { data, loading };
}

function computeScoreAndSuggestions(data) {
  if (!data) return { percent: 0, suggestions: [], breakdown: [] };

  const ctx = { accountLimit: EMAIL_ACCOUNT_LIMITS[data.emailPlan] || 5, emailPlan: data.emailPlan };
  let percent = 0;
  const suggestions = [];
  const breakdown = [];

  COCKPIT_CHECKS.forEach((item, index) => {
    const done = item.check(data, ctx);
    if (done) percent += item.weight;
    else {
      if (item.id === 'multipleAccounts' && ctx.accountLimit <= 1) return;
      const text = typeof item.text === 'function' ? item.text(data, ctx) : item.text;
      suggestions.push({ id: item.id, tab: item.tab, text, priority: index + 1, icon: item.icon });
    }
    const label = typeof item.text === 'function' ? item.text(data, ctx) : item.text;
    breakdown.push({ id: item.id, done, tab: item.tab, label, icon: item.icon });
  });

  suggestions.sort((a, b) => a.priority - b.priority);
  percent = Math.round(Math.min(100, percent));
  return { percent, suggestions, breakdown };
}

function membershipPlanConfig(creditPlan, userMembershipPlan) {
  const id = resolveMembershipPlanId(creditPlan, userMembershipPlan ? { membershipPlan: userMembershipPlan } : null);
  return id ? CREDIT_PLANS[id] : null;
}

function getPlanDetails(creditPlan, userMembershipPlan) {
  const membership = membershipPlanConfig(creditPlan, userMembershipPlan);
  const resolved = membership || CREDIT_PLANS.starter;
  const labels = { starter: 'STARTER', pro: 'PRO', enterprise: 'MAX' };
  const taglines = {
    starter: 'Everything you need to get started.',
    pro: 'Full power for your pipeline.',
    enterprise: 'Maximum potential unlocked.',
  };
  return {
    label: membership ? (labels[membership.id] || membership.label) : 'NO PLAN',
    tagline: membership
      ? (taglines[membership.id] || 'You\'re using SalesGod.')
      : 'Activate a membership to unlock a credit plan.',
    gradient: resolved.gradient,
    bgGradient: resolved.bgGradient,
    emailsPerDay: membership
      ? (membership.features?.emailsPerDay || 0)
      : CREDIT_PLANS.starter.features.emailsPerDay,
    monthlyCredits: membership ? membership.monthlyCredits : 0,
    hasMembership: !!membership,
  };
}

const PLAN_CARD_ICONS = {
  starter: '/img/salesgod_light.webp',
  pro: '/img/globus.svg',
  enterprise: '/img/promax.svg',
};

/** Starter/Pro/Enterprise come only from an active membership. */
function getPlanTier(creditPlan, userMembershipPlan) {
  const membership = membershipPlanConfig(creditPlan, userMembershipPlan);
  return membership ? membership.id : 'starter';
}

/** Feature status: done, open, or plan (requires higher tier) */
function getFeatureStatus(featureId, breakdown, planTier, data) {
  const feature = FEATURE_CATALOG.find((f) => f.id === featureId);
  if (!feature) return { status: 'open', label: 'Set up needed' };
  if (feature.planRequired) {
    const order = ['starter', 'pro', 'enterprise'];
    const userLevel = order.indexOf(planTier);
    const requiredLevel = order.indexOf(feature.planRequired);
    if (userLevel < requiredLevel) return { status: 'plan', label: feature.planRequired === 'enterprise' ? 'Enterprise' : 'Pro' };
  }
  const b = breakdown.find((x) => x.id === featureId);
  if (b && b.done) return { status: 'done', label: 'Done' };
  if (data) {
    if (featureId === 'customFields' && hasKeys(data.customFields)) return { status: 'done', label: 'Done' };
    if (featureId === 'extraSettings' && data.moreSettings && (data.moreSettings.calendlyBookingLink || objectLength(data.moreSettings) > 0)) return { status: 'done', label: 'Done' };
    if (featureId === 'whatsapp' && hasKeys(data.whatsappSettings)) return { status: 'done', label: 'Done' };
    if (featureId === 'blacklist' && data.hasBlacklist) return { status: 'done', label: 'Done' };
  }
  return { status: 'open', label: 'Set up needed' };
}

const FEATURE_PREVIEW_LIMIT = 4;

function getSectionFeatures(tier, isOwner, breakdown, planTier, data) {
  return FEATURE_CATALOG
    .filter((f) => f.tier === tier && !(f.id === 'team' && !isOwner))
    .map((f) => ({ ...f, ...getFeatureStatus(f.id, breakdown, planTier, data) }));
}

function FeatureSectionPreview({ features, limit = FEATURE_PREVIEW_LIMIT }) {
  const shown = features.slice(0, limit);
  const more = features.length - shown.length;
  return (
    <span className="cockpit__featuresPreview">
      {shown.map((f) => (
        <span key={f.id} className={`cockpit__featuresPreviewChip cockpit__featuresPreviewChip--${f.status}`}>
          <i className={f.icon} aria-hidden />
          <span className="cockpit__featuresPreviewChipTitle">{f.title}</span>
          <i
            className={`fa ${f.status === 'done' ? 'fa-check-circle' : f.status === 'plan' ? 'fa-lock' : 'fa-circle'} cockpit__featuresPreviewChipStatus`}
            aria-hidden
          />
        </span>
      ))}
      {more > 0 && <span className="cockpit__featuresPreviewMore">+{more} more</span>}
    </span>
  );
}

const todayKey = () => new Date().toISOString().slice(0, 10);

function dayKeysRange(daysBack, count) {
  const keys = [];
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  for (let i = 0; i < count; i++) {
    keys.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() - 1);
  }
  return keys;
}

function computeActivity(emailHealth) {
  const empty = { todaySent: 0, weekSent: 0, lastWeekSent: 0, totalSent: 0, totalReplied: 0, positiveReplies: 0, accounts: [] };
  if (!emailHealth || typeof emailHealth !== 'object') return empty;

  let todaySent = 0;
  let weekSent = 0;
  let lastWeekSent = 0;
  let totalSent = 0;
  let totalReplied = 0;
  let positiveReplies = 0;
  const key = todayKey();
  const weekKeys = dayKeysRange(0, 7);
  const prevWeekKeys = dayKeysRange(7, 7);
  const accounts = [];

  Object.entries(emailHealth).forEach(([accId, acc]) => {
    if (!acc || typeof acc !== 'object') return;
    const accTotal = acc.totalSent || 0;
    const accReplied = acc.totalReplied || 0;
    // Stored RTDB counter. Incremented when the AI scores a reply. Not an inbox scan.
    const accPositive = acc.totalPositiveReplied || 0;
    totalSent += accTotal;
    totalReplied += accReplied;
    positiveReplies += accPositive;
    const dailyCounts = acc.dailyCounts || {};

    let accToday = 0;
    let accWeek = 0;
    let lastSentDate = null;

    weekKeys.forEach((dk) => {
      const d = dailyCounts[dk];
      if (d && typeof d === 'object') accWeek += d.sent || 0;
    });
    prevWeekKeys.forEach((dk) => {
      const d = dailyCounts[dk];
      if (d && typeof d === 'object') lastWeekSent += d.sent || 0;
    });

    const todayDaily = dailyCounts[key];
    if (todayDaily && typeof todayDaily === 'object') accToday = todayDaily.sent || 0;

    const sortedDays = Object.keys(dailyCounts).sort().reverse();
    for (const dk of sortedDays) {
      const d = dailyCounts[dk];
      if (d && typeof d === 'object' && (d.sent || 0) > 0) { lastSentDate = dk; break; }
    }

    todaySent += accToday;
    weekSent += accWeek;

    let daysSinceLastSend = null;
    if (lastSentDate) {
      const diff = (new Date(key) - new Date(lastSentDate)) / 86400000;
      daysSinceLastSend = Math.round(diff);
    }

    accounts.push({
      id: accId,
      email: acc.email || acc.originalEmail || accId,
      todaySent: accToday,
      totalSent: accTotal,
      totalReplied: accReplied,
      daysSinceLastSend,
      status: daysSinceLastSend === null ? 'new' : daysSinceLastSend >= 3 ? 'idle' : 'active',
    });
  });

  return { todaySent, weekSent, lastWeekSent, totalSent, totalReplied, positiveReplies, accounts };
}

/**
 * Selectable windows for the activity strip. All numbers are summed from the
 * `dailyCounts` buckets already loaded with emailHealth, so switching the range
 * costs no extra reads.
 */
const ACTIVITY_RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'custom', label: 'Custom range' },
];
const DEFAULT_ACTIVITY_RANGE = 'last30';
const ACTIVITY_RANGE_STORAGE_KEY = 'cockpit.activityRange';
/** Mirrors POSITIVE_REPLY_MIN_SCORE in functions/helpers/emailHealthHelper.js */
const POSITIVE_REPLY_MIN_SCORE = 6;
/** Guards against a custom range spanning years of buckets */
const MAX_RANGE_DAYS = 400;

/** dailyCounts keys are UTC dates — see emailHealthHelper.todayKey() */
const dateKey = (date) => date.toISOString().slice(0, 10);

function shiftedDayKey(daysBack) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysBack);
  return dateKey(d);
}

/** Resolves a range id into inclusive UTC date keys, or null if incomplete */
function resolveActivityRange(rangeId, customStart, customEnd) {
  const now = new Date();
  const today = dateKey(now);

  switch (rangeId) {
    case 'today':
      return { startKey: today, endKey: today };
    case 'last7':
      return { startKey: shiftedDayKey(6), endKey: today };
    case 'last90':
      return { startKey: shiftedDayKey(89), endKey: today };
    case 'thisMonth':
      return { startKey: dateKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))), endKey: today };
    case 'lastMonth': {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
      return { startKey: dateKey(start), endKey: dateKey(end) };
    }
    case 'custom':
      if (!customStart || !customEnd || customStart > customEnd) return null;
      return { startKey: customStart, endKey: customEnd };
    default:
      return { startKey: shiftedDayKey(29), endKey: today };
  }
}

function dayKeysBetween(startKey, endKey) {
  const keys = [];
  const cursor = new Date(`${startKey}T00:00:00Z`);
  const end = new Date(`${endKey}T00:00:00Z`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return keys;
  while (cursor <= end && keys.length < MAX_RANGE_DAYS) {
    keys.push(dateKey(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return keys;
}

/** The equally long window right before the range, used for the trend arrow */
function precedingWindow(startKey, dayCount) {
  const end = new Date(`${startKey}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() - 1);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (dayCount - 1));
  return { startKey: dateKey(start), endKey: dateKey(end) };
}

function sumDailyCounts(emailHealth, dayKeys) {
  const totals = { sent: 0, replied: 0, positiveReplies: 0 };
  if (!emailHealth || typeof emailHealth !== 'object' || !dayKeys.length) return totals;

  Object.values(emailHealth).forEach((acc) => {
    if (!acc || typeof acc !== 'object') return;
    const dailyCounts = acc.dailyCounts || {};
    dayKeys.forEach((dk) => {
      const day = dailyCounts[dk];
      if (!day || typeof day !== 'object') return;
      totals.sent += day.sent || 0;
      totals.replied += day.replied || 0;
      totals.positiveReplies += day.positiveReplied || 0;
    });
  });

  return totals;
}

function computeRangeActivity(emailHealth, range) {
  const empty = { sent: 0, replied: 0, positiveReplies: 0, prevSent: 0, days: 0 };
  if (!range) return empty;

  const dayKeys = dayKeysBetween(range.startKey, range.endKey);
  if (!dayKeys.length) return empty;

  const prev = precedingWindow(range.startKey, dayKeys.length);
  const totals = sumDailyCounts(emailHealth, dayKeys);

  return {
    ...totals,
    prevSent: sumDailyCounts(emailHealth, dayKeysBetween(prev.startKey, prev.endKey)).sent,
    days: dayKeys.length,
  };
}

/** Personal rank based on all-time positive replies (score >= POSITIVE_REPLY_MIN_SCORE) */
const RANK_TIERS = [
  { min: 0, id: 'starter', label: 'Starter', icon: 'fa-flag', nextAt: 3,
    description: 'You\'re just getting started. Every positive reply counts.' },
  { min: 3, id: 'bronze', label: 'Bronze', icon: 'fa-medal', nextAt: 10,
    description: 'First replies are in. Keep the momentum going.' },
  { min: 10, id: 'silver', label: 'Silver', icon: 'fa-medal', nextAt: 50,
    description: 'Ten yeses in. The inbox is starting to like you back.' },
  { min: 50, id: 'gold', label: 'Gold', icon: 'fa-trophy', nextAt: 200,
    description: 'Fifty people wrote back on purpose. That is not luck.' },
  { min: 200, id: 'platinum', label: 'Platinum', icon: 'fa-crown', nextAt: 1000,
    description: 'Cold emails, warm replies. At this point it is a habit.' },
  { min: 1000, id: 'emerald', label: 'Emerald', icon: 'fa-leaf', nextAt: 10000,
    description: 'A thousand. Your pipeline shows up to work without you.' },
  { min: 10000, id: 'diamond', label: 'Diamond', icon: 'fa-gem', nextAt: 100000,
    description: 'Top 0.1%. The other 99.9% should take notes.' },
  { min: 100000, id: 'legend', label: 'Legend', icon: 'fa-star', nextAt: Infinity,
    description: 'There is no next rank. You are the next rank.' },
];
function getRank(totalReplied) {
  for (let i = RANK_TIERS.length - 1; i >= 0; i--) {
    if (totalReplied >= RANK_TIERS[i].min) return RANK_TIERS[i];
  }
  return RANK_TIERS[0];
}

/** Context-aware recommendations (replace generic "next steps") */
function computeSmartRecs(data, suggestions, todaySent) {
  if (!data) return [];
  const recs = [];
  const topicsTotal = objectLength(data.selectedTopics) + objectLength(data.customTopics);
  const agentsCount = objectLength(data.agents);

  if (topicsTotal > 0 && agentsCount === 0) {
    recs.push({ id: 'rec-agents', icon: 'fa fa-brain', tab: 'agents',
      text: 'You\'re sending emails but have no Inbox Reply cases. Replies land unanswered.', cta: 'Define cases' });
  }

  if (data.emailAccountsCount >= 2 && data.warmupAccountsCount === 0) {
    recs.push({ id: 'rec-warmup', icon: 'fa fa-fire', tab: 'trueWarmup',
      text: 'Multiple accounts connected but no warmup. Deliverability could suffer.', cta: 'Activate warmup' });
  }

  if (todaySent > 5 && topicsTotal <= 1) {
    recs.push({ id: 'rec-topics', icon: 'fa fa-file-alt', tab: 'topics',
      text: 'You\'re sending volume. Try multiple templates to increase response rates.', cta: 'Add templates' });
  }

  if (topicsTotal > 0 && !hasKeys(data.leadFlows)) {
    recs.push({ id: 'rec-flows', icon: 'fa fa-project-diagram', tab: 'leadSettings',
      text: 'Automate follow-ups with Lead Flows so leads don\'t fall through the cracks.', cta: 'Create a flow' });
  }

  if (!data.footerSettings || !(data.footerSettings.footer_text && String(data.footerSettings.footer_text).trim())) {
    recs.push({ id: 'rec-footer', icon: 'fa fa-align-left', tab: 'emailPreferences',
      text: 'A professional footer improves trust and keeps you compliant.', cta: 'Set up footer' });
  }

  suggestions.forEach((s) => {
    if (!recs.find((r) => r.tab === s.tab)) {
      recs.push({ id: `sug-${s.id}`, icon: s.icon, tab: s.tab, text: s.text, cta: 'Set up' });
    }
  });

  return recs.slice(0, 6);
}

/**
 * Smart hero intelligence: picks headline, contextual insight, and prioritized
 * chip-suggestions based on the actual state of the user's world — not just a
 * static percent-to-text mapping. Considers activity velocity, idle accounts,
 * quota usage, time-of-day, and bottleneck detection.
 */
function computeHeroIntelligence(percent, data, activity, suggestions, breakdown, planDetails) {
  if (!data) return { headline: 'Loading…', insight: '', chips: [] };

  const { todaySent, weekSent, lastWeekSent, positiveReplies: totalReplied, accounts } = activity;
  const idleAccounts = accounts.filter((a) => a.status === 'idle');
  const hour = new Date().getHours();
  const isWeekend = [0, 6].includes(new Date().getDay());
  const topicsTotal = objectLength(data.selectedTopics) + objectLength(data.customTopics);
  const agentsCount = objectLength(data.agents);
  const doneCount = breakdown.filter((b) => b.done).length;
  const openCount = breakdown.filter((b) => !b.done).length;
  const hasWarmup = data.warmupAccountsCount >= 1;
  const hasSafeGuard = hasKeys(data.safeGuardSettings);
  const hasFlows = hasKeys(data.leadFlows);
  const weekTrend = lastWeekSent > 0 ? Math.round(((weekSent - lastWeekSent) / lastWeekSent) * 100) : 0;
  const quotaPct = planDetails.emailsPerDay > 0 ? Math.round((todaySent / planDetails.emailsPerDay) * 100) : 0;

  let headline = '';
  let insight = '';

  if (percent >= 100) {
    if (todaySent === 0 && hour >= 11 && !isWeekend) {
      headline = 'All set, waiting for action';
      insight = 'Setup is perfect. No emails sent today. Time to fill the pipeline.';
    } else if (weekTrend > 20 && weekSent > 10) {
      headline = 'Full power & accelerating';
      insight = `Volume is up ${weekTrend}% this week.${totalReplied > 0 ? ` ${totalReplied} replies so far.` : ''}`;
    } else if (idleAccounts.length > 0) {
      headline = 'Fully loaded. Check your accounts';
      insight = `${idleAccounts.length} account${idleAccounts.length > 1 ? 's' : ''} went quiet. Everything else is perfect.`;
    } else if (quotaPct >= 80) {
      headline = 'Running at full throttle';
      insight = `${quotaPct}% of today's quota used. Maximum volume.`;
    } else {
      headline = 'Maximum potential';
      insight = 'Every feature is live. SalesGod is running at full capacity.';
    }
  } else if (percent >= 80) {
    if (idleAccounts.length > 0 && !hasWarmup) {
      headline = 'Almost there. Warmup could help';
      insight = `${idleAccounts.length} idle account${idleAccounts.length > 1 ? 's' : ''}. Warmup keeps deliverability strong.`;
    } else if (openCount === 1) {
      const lastItem = suggestions[0];
      headline = 'One step away';
      insight = lastItem ? `Just this: ${lastItem.text.toLowerCase()}.` : 'One final tweak and you hit 100%.';
    } else {
      headline = 'Almost there';
      insight = `${openCount} tweaks left. You're already at ${percent}% potential.`;
    }
  } else if (percent >= 60) {
    if (todaySent > 0 && !hasWarmup) {
      headline = 'Active, but unprotected';
      insight = 'You\'re sending without warmup. Deliverability could drop over time.';
    } else if (todaySent > 5 && topicsTotal <= 1) {
      headline = 'Volume is there, variety isn\'t';
      insight = 'Only one template active. More variety means higher response rates.';
    } else if (idleAccounts.length >= 2) {
      headline = 'Good setup. Accounts need attention';
      insight = `${idleAccounts.length} accounts haven't sent in 3+ days.`;
    } else {
      headline = 'Strong foundation';
      insight = `Core is solid. ${openCount} remaining steps unlock the full potential.`;
    }
  } else if (percent >= 30) {
    if (data.emailAccountsCount === 0) {
      headline = 'Connect an email first';
      insight = 'Nothing works without at least one email account.';
    } else if (topicsTotal === 0) {
      headline = 'Account ready. Templates missing';
      insight = 'Email connected, but the AI needs a template to start writing.';
    } else {
      headline = 'Building up';
      insight = `${doneCount} done, ${openCount} to go. Focus on the essentials next.`;
    }
  } else {
    if (data.emailAccountsCount === 0) {
      headline = 'Let\'s get you started';
      insight = 'Step one: connect an email account. Everything flows from there.';
    } else {
      headline = 'Just beginning';
      insight = 'Each step multiplies what SalesGod can do for you.';
    }
  }

  // Chips ranked by actual impact — highest score wins, deduplicated by tab
  const chipCandidates = [];

  if (data.emailAccountsCount === 0) {
    chipCandidates.push({ icon: 'fa fa-envelope', text: 'Connect email', tab: 'email', score: 100 });
  }
  if (topicsTotal === 0 && data.emailAccountsCount > 0) {
    chipCandidates.push({ icon: 'fa fa-file-alt', text: 'Create template', tab: 'topics', score: 95 });
  }
  if (!data.aiSettings?.model && data.emailAccountsCount > 0) {
    chipCandidates.push({ icon: 'fa fa-magic', text: 'Configure AI', tab: 'ai', score: 85 });
  }
  if (data.emailAccountsCount >= 1 && !hasWarmup) {
    chipCandidates.push({ icon: 'fa fa-fire', text: 'Start warmup', tab: 'trueWarmup', score: todaySent > 0 ? 88 : 55 });
  }
  if (todaySent > 0 && !hasSafeGuard) {
    chipCandidates.push({ icon: 'fa fa-shield-alt', text: 'Enable SafeGuard', tab: 'safeGuard', score: 72 });
  }
  if (topicsTotal > 0 && agentsCount === 0) {
    chipCandidates.push({ icon: 'fa fa-brain', text: 'Define reply cases', tab: 'agents', score: 68 });
  }
  if (topicsTotal > 0 && !hasFlows) {
    chipCandidates.push({ icon: 'fa fa-project-diagram', text: 'Create flow', tab: 'leadSettings', score: 50 });
  }
  if (idleAccounts.length > 0) {
    chipCandidates.push({ icon: 'fa fa-clock', text: `${idleAccounts.length} idle`, tab: 'email', score: 62 });
  }
  if (!data.footerSettings || !(data.footerSettings.footer_text && String(data.footerSettings.footer_text).trim())) {
    chipCandidates.push({ icon: 'fa fa-align-left', text: 'Add footer', tab: 'emailPreferences', score: 38 });
  }

  chipCandidates.sort((a, b) => b.score - a.score);
  const seenTabs = new Set();
  const chips = [];
  for (const c of chipCandidates) {
    if (seenTabs.has(c.tab)) continue;
    seenTabs.add(c.tab);
    chips.push(c);
    if (chips.length >= 3) break;
  }

  return { headline, insight, chips };
}

export default function WorldSettingsCockpit({ onOpenTab }) {
  const navigate = useNavigate();
  const { userId, isOwner, isMember, isMasterMember, currentUserId, teamOwnerName, activeTeam } = useActiveWorld();
  const isWorldOwner = !!currentUserId && (currentUserId === userId || activeTeam?.isOwnAccount === true);
  const showCollab = !!currentUserId && currentUserId !== userId && !activeTeam?.isOwnAccount;
  const [ownerProfile, setOwnerProfile] = useState({ photo: '', name: '' });
  const [selfProfile, setSelfProfile] = useState({ photo: '', name: '' });
  // Two-phase open/close so the vault can play its own exit animation before
  // unmounting and revealing the cockpit again. `vaultOpen` controls mounting,
  // `vaultClosing` flags the running exit animation.
  const [vaultOpen, setVaultOpen] = useState(false);
  const [ultimateSetupOpen, setUltimateSetupOpen] = useState(false);
  const [vaultClosing, setVaultClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const { data, loading } = useCockpitData(userId);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async (uid) => {
      if (!uid) return { photo: '', name: '' };
      const snap = await get(ref(database, `Users/${uid}`));
      if (!snap.exists()) return { photo: '', name: '' };
      const u = snap.val() || {};
      return {
        photo: u.userPhoto || '',
        name: u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '',
      };
    };
    (async () => {
      try {
        const [owner, self] = await Promise.all([
          loadProfile(userId),
          currentUserId && currentUserId !== userId ? loadProfile(currentUserId) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setOwnerProfile(owner);
        setSelfProfile(self || owner);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [userId, currentUserId]);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleOpenVault = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setVaultClosing(false);
    setVaultOpen(true);
  }, []);

  const handleCloseVault = useCallback(() => {
    setVaultClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setVaultOpen(false);
      setVaultClosing(false);
      closeTimerRef.current = null;
    }, 260); // matches CSS exit animation duration
  }, []);
  const {
    balance: creditBalance,
    monthlyBalance,
    bonusBalance,
    sharedPool: creditsAreShared,
    lastUpdated: creditsLastUpdated,
  } = useCreditBalance(userId);
  const { categories: academyCategories, videos: academyVideos } = useAcademyMedia();
  const { percent, suggestions, breakdown } = useMemo(() => computeScoreAndSuggestions(data), [data]);
  const foundation = useMemo(() => deriveUltimateFoundationStatus(data), [data]);
  const planTier = useMemo(
    () => getPlanTier(data?.creditPlan, data?.membershipPlan),
    [data?.creditPlan, data?.membershipPlan]
  );
  const activity = useMemo(() => computeActivity(data?.emailHealth), [data?.emailHealth]);
  const { todaySent, totalSent, positiveReplies } = activity;
  const rank = useMemo(() => getRank(positiveReplies), [positiveReplies]);
  const [showRankInfo, setShowRankInfo] = useState(false);
  const [showUpgradePlans, setShowUpgradePlans] = useState(false);
  const [potentialOpen, setPotentialOpen] = useState(false);
  const [basicsOpen, setBasicsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [activityRange, setActivityRange] = useState(() => {
    try {
      const stored = localStorage.getItem(ACTIVITY_RANGE_STORAGE_KEY);
      if (stored && ACTIVITY_RANGES.some((r) => r.id === stored)) return stored;
    } catch (_) {}
    return DEFAULT_ACTIVITY_RANGE;
  });
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  // A custom range is bound to the two date inputs, so remembering it across
  // reloads would restore an empty selection.
  const handleRangeChange = useCallback((next) => {
    setActivityRange(next);
    try {
      if (next === 'custom') localStorage.removeItem(ACTIVITY_RANGE_STORAGE_KEY);
      else localStorage.setItem(ACTIVITY_RANGE_STORAGE_KEY, next);
    } catch (_) {}
  }, []);
  const resolvedRange = useMemo(
    () => resolveActivityRange(activityRange, customStart, customEnd),
    [activityRange, customStart, customEnd]
  );
  const rangeActivity = useMemo(
    () => computeRangeActivity(data?.emailHealth, resolvedRange),
    [data?.emailHealth, resolvedRange]
  );
  const last7Activity = useMemo(
    () => computeRangeActivity(data?.emailHealth, resolveActivityRange('last7')),
    [data?.emailHealth]
  );
  const planDetails = useMemo(
    () => getPlanDetails(data?.creditPlan, data?.membershipPlan),
    [data?.creditPlan, data?.membershipPlan]
  );
  const accountSinceText = useMemo(() => {
    const raw = data?.accountCreatedAt;
    if (!raw) return '';
    const date = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }, [data?.accountCreatedAt]);
  const creditMeter = useMemo(() => {
    if (creditBalance == null) return null;
    const total = planDetails.hasMembership ? (planDetails.monthlyCredits || 0) : 0;
    const monthlyRemaining = monthlyBalance ?? creditBalance;
    const extra = bonusBalance || 0;
    const remaining = monthlyRemaining;
    const remainingPct = total > 0 ? Math.min(100, Math.round((monthlyRemaining / total) * 100)) : 0;
    const barTone = !total
      ? 'ok'
      : remainingPct <= 10 ? 'low' : remainingPct <= 30 ? 'warn' : 'ok';
    const refilledThisCycle = wasRefilledThisCycle(data?.creditPlan);
    const refillAt = resolveNextCreditCycle(data?.creditPlan);
    const lastChanged = creditsLastUpdated
      ? ` Last changed ${new Date(creditsLastUpdated).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.`
      : '';
    let refillText = '';
    let refillTitle = '';
    let resetNote = '';
    if (refillAt) {
      const daysLeft = daysUntil(refillAt);
      const refillLabel = formatCreditCycleDate(refillAt);
      const refillFull = refillAt.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Europe/Vienna',
      });
      refillText = daysLeft === 0
        ? `Resets today · ${refillLabel}`
        : `New credits ${refillLabel} · in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
      resetNote = ROLLOVER_RATIO === 0
        ? `On ${refillFull} the wallet resets to ${total.toLocaleString()}. Unused credits do not carry over.`
        : `On ${refillFull} the wallet is topped back up to the monthly allowance.`;
      refillTitle = resetNote;
    }
    const remainingLabel = refilledThisCycle
      ? `${remaining.toLocaleString()} remaining this cycle`
      : `${remaining.toLocaleString()} in wallet`;
    const remainingTitle = refilledThisCycle
      ? `Current wallet after this membership refill. ${resetNote}${lastChanged}`
      : `Current wallet snapshot, not a count of emails or actions this month. ${resetNote}${lastChanged}`;
    return {
      total,
      remaining,
      remainingPct,
      barTone,
      extra,
      remainingLabel,
      remainingTitle,
      refillText,
      refillTitle: resetNote,
    };
  }, [planDetails.monthlyCredits, planDetails.hasMembership, creditBalance, monthlyBalance, bonusBalance, data?.creditPlan, creditsLastUpdated]);
  const smartRecs = useMemo(() => computeSmartRecs(data, suggestions, todaySent), [data, suggestions, todaySent]);
  const heroIntel = useMemo(
    () => computeHeroIntelligence(percent, data, activity, suggestions, breakdown, planDetails),
    [percent, data, activity, suggestions, breakdown, planDetails]
  );

  useEffect(() => {
    if (!potentialOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setPotentialOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [potentialOpen]);

  useEffect(() => {
    if (!showRankInfo) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowRankInfo(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [showRankInfo]);

  useEffect(() => {
    if (!showRankInfo) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowRankInfo(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [showRankInfo]);

  useEffect(() => {
    if (!academyVideos.length) return;
    const matches = FEATURE_CATALOG.map((f) => {
      const m = getFeatureMedia(f.id, f.title, f.academyCategoryId, academyCategories, academyVideos);
      const matchedVideo = matchFeatureToVideo(f.id, f.title, academyVideos);
      return { feature: f.id, matchedVideo: matchedVideo?.video?.title || '—', hasImage: !!m.image, image: m.image?.substring(0, 60) };
    });
    console.log('[Cockpit] Feature→Video matching:', matches);
  }, [academyCategories, academyVideos]);

  if (!userId || loading) {
    return (
      <div className="cockpit cockpit--loading">
        <div className="cockpit__skeleton">
          <div className="cockpit__skelHeader">
            <div className="cockpit__skelLines">
              <div className="cockpit__skelLine cockpit__skelLine--title" />
              <div className="cockpit__skelLine cockpit__skelLine--sub" />
              <div className="cockpit__skelLine cockpit__skelLine--btn" />
            </div>
            <div className="cockpit__skelCircle" />
          </div>
          <div className="cockpit__skelBar" />
          <div className="cockpit__skelGrid">
            <div className="cockpit__skelCard cockpit__skelCard--wide" />
            <div className="cockpit__skelCard" />
          </div>
          <div className="cockpit__skelRow">
            <div className="cockpit__skelStat" />
            <div className="cockpit__skelStat" />
            <div className="cockpit__skelStat" />
            <div className="cockpit__skelStat" />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="cockpit cockpit--loading">
        <p className="cockpit__loadingText">Could not load data.</p>
        <button type="button" className="cockpit__retryBtn" onClick={() => window.location.reload()}>Reload page</button>
      </div>
    );
  }

  // When the Account Brain is open, it owns the entire cockpit surface –
  // no popup, no overlay. Returns to cockpit via the back button or Esc.
  if (vaultOpen) {
    return (
      <div className="cockpit cockpit--vaultMode">
        <CockpitDataVault
          mode="fullscreen"
          userId={userId}
          currentUserId={currentUserId}
          canEdit={!!isOwner}
          closing={vaultClosing}
          onClose={handleCloseVault}
        />
      </div>
    );
  }

  // Same pattern as the Vault: Ultimate Setup takes over the cockpit surface
  // as a real sub-page instead of a modal overlay.
  if (ultimateSetupOpen) {
    return (
      <div className="cockpit cockpit--ultimateMode">
        <UltimateSetupModal
          mode="fullscreen"
          userId={userId}
          foundationDiagnostics={foundation}
          onClose={() => setUltimateSetupOpen(false)}
          onOpenTab={onOpenTab}
        />
      </div>
    );
  }

  const accountLimit = EMAIL_ACCOUNT_LIMITS[data.emailPlan] || 5;
  const topicsCount = (data.selectedTopics && Object.keys(data.selectedTopics).length) || 0;
  const customTopicsCount = (data.customTopics && Object.keys(data.customTopics).length) || 0;
  const agentsCount = (data.agents && typeof data.agents === 'object' ? Object.keys(data.agents).length : 0) || 0;
  const flowsCount = data.leadFlows && typeof data.leadFlows === 'object' ? Object.keys(data.leadFlows).length : 0;
  const doneCount = breakdown.filter((b) => b.done).length;
  const totalCount = breakdown.length;

  const sentTrend = rangeActivity.prevSent > 0
    ? Math.round(((rangeActivity.sent - rangeActivity.prevSent) / rangeActivity.prevSent) * 100)
    : 0;
  // Cold outreach reply rates sit low single digits, so one decimal matters.
  const replyRate = rangeActivity.sent > 0
    ? Math.round((rangeActivity.replied / rangeActivity.sent) * 1000) / 10
    : 0;
  const rangeLabel = !resolvedRange
    ? 'Pick two dates'
    : activityRange === 'today'
      ? 'Today'
      : `${rangeActivity.days} day${rangeActivity.days === 1 ? '' : 's'}`;
  const sentWindowHint = [
    activityRange !== 'today' ? `${todaySent} today` : null,
    activityRange !== 'last7' ? `${last7Activity.sent} last 7d` : null,
  ].filter(Boolean).join(' · ');

  const quotaPct = planDetails.emailsPerDay > 0
    ? Math.min(100, Math.round((todaySent / planDetails.emailsPerDay) * 100))
    : 0;
  const quotaTone = quotaPct >= 90 ? 'low' : quotaPct >= 70 ? 'warn' : 'ok';

  const heroCompact = percent >= HERO_COMPACT_MIN_PERCENT;
  const basicsFeatures = getSectionFeatures('essential', isOwner, breakdown, planTier, data);
  const advancedFeatures = getSectionFeatures('extra', isOwner, breakdown, planTier, data);
  const basicsDone = basicsFeatures.filter((f) => f.status === 'done').length;
  const advancedDone = advancedFeatures.filter((f) => f.status === 'done').length;

  return (
    <div className="cockpit">
      <div className="cockpit__back" />
      <div className="cockpit__surface">
        {/* SalesGod potential – top. Once the setup is basically done it leaves
            the flow entirely and lives in the floating bubble (bottom right). */}
        {!heroCompact && doneCount >= 2 && (
        <section className="cockpit__heroCard">
          <div className="cockpit__heroTop">
            <div className="cockpit__heroHeadline">
              <span className="cockpit__heroLabel">{heroIntel.headline}</span>
              <span className="cockpit__heroInsight">{heroIntel.insight}</span>
              {suggestions.length > 0 && (
                <button
                  type="button"
                  className="cockpit__heroNextStep"
                  onClick={() => onOpenTab?.(suggestions[0].tab)}
                >
                  <i className={suggestions[0].icon} aria-hidden />
                  {suggestions[0].text}
                  <i className="fa fa-chevron-right" aria-hidden />
                </button>
              )}
            </div>
            <div className="cockpit__heroValueWrap">
              <span className="cockpit__heroValue">{percent}</span>
              <span className="cockpit__heroUnit">%</span>
            </div>
          </div>
          <div className="cockpit__heroBarWrap">
            <div className="cockpit__progressTrack cockpit__progressTrack--hero">
              <div
                className="cockpit__progressBar cockpit__progressBar--hero"
                style={{ width: `${percent}%`, background: percent >= 100 ? '#34c759' : undefined }}
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="cockpit__heroMeta">
              <span><span className="cockpit__heroMetaHighlight">{doneCount}</span> of {totalCount} complete</span>
              {totalCount - doneCount > 0 && <span>{totalCount - doneCount} to go</span>}
            </div>
          </div>
        </section>
        )}

        {/* Godly Setup: only while 2 or fewer checklist steps are done */}
        {doneCount <= 2 && (
          <section className="cockpit__ultimateCta" aria-label="Ultimate Setup">
            <div className="cockpit__ultimateCtaBody">
              <span className="cockpit__ultimateCtaBadge">Powered by BonusClub AI</span>
              <h3 className="cockpit__ultimateCtaTitle">SalesGod Intelligent Setup</h3>
              <p className="cockpit__ultimateCtaSubtitle">
                {foundation.allDone
                  ? 'Your foundation is ready. You can still run setup again anytime.'
                  : 'Describe your business once. SalesGod builds the rest.'}
              </p>
              <div className="cockpit__ultimateCtaPills">
                {FOUNDATION_ORDER.map((fid) => {
                  const c = foundation.checks[fid];
                  if (!c) return null;
                  return (
                    <span
                      key={fid}
                      className={`cockpit__ultimateCtaPill ${c.ok ? 'cockpit__ultimateCtaPill--done' : 'cockpit__ultimateCtaPill--missing'}`}
                      title={c.label}
                    >
                      <i className={`fa ${c.icon}`} aria-hidden />
                      {c.pillShort}
                      <i className={`fa ${c.ok ? 'fa-check' : 'fa-circle'} cockpit__ultimateCtaPillTick`} aria-hidden />
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="cockpit__ultimateCtaActions">
              <button
                type="button"
                className="cockpit__ultimateCtaAction"
                onClick={() => setUltimateSetupOpen(true)}
              >
                <span className="cockpit__ultimateCtaActionIcon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="7" width="20" height="11" rx="2" stroke="currentColor" strokeWidth="1.75" />
                    <path d="M6 11h.01M10 11h.01M14 11h.01M18 11h.01M8 15h8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="cockpit__ultimateCtaActionCopy">
                  <span className="cockpit__ultimateCtaActionLabel">Text setup</span>
                </span>
              </button>
              <div className="cockpit__ultimateCtaAction cockpit__ultimateCtaAction--soon" aria-disabled="true">
                <span className="cockpit__ultimateCtaActionIcon" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none">
                    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.75" />
                    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="cockpit__ultimateCtaActionCopy">
                  <span className="cockpit__ultimateCtaActionLabel">Speech setup</span>
                  <span className="cockpit__ultimateCtaSoon">Coming soon</span>
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Two columns: left = Account, Activity, KPIs, Next steps | right = Checklist */}
        <div className="cockpit__mainGrid">
          <div className="cockpit__mainLeft">
            <div className={`cockpit__planUsage cockpit__planUsage--${planTier}${showCollab ? ' cockpit__planUsage--guest' : ''}`}>
              <div className="cockpit__planCard">
                <div className="cockpit__planCardShine" />
                <div className="cockpit__planCardBody">
                  <div className="cockpit__planCardIdentity">
                    <div className="cockpit__planCardNameRow">
                      <span className="cockpit__planCardLabel">
                        {ownerProfile.name || teamOwnerName || planDetails.label}
                      </span>
                      <span className={`cockpit__planCardBadge cockpit__planCardBadge--inline cockpit__planCardBadge--${planTier}`}>
                        <span>{planDetails.label}</span>
                        {PLAN_CARD_ICONS[planTier] ? (
                          <img src={PLAN_CARD_ICONS[planTier]} alt="" className="cockpit__planCardBadgeIcon" />
                        ) : null}
                      </span>
                      <button
                        type="button"
                        className="cockpit__planCardUpgrade"
                        onClick={() => setShowUpgradePlans(true)}
                      >
                        Upgrade Account
                      </button>
                    </div>
                    {isWorldOwner && accountSinceText && (
                      <span className="cockpit__planCardSince">Member since {accountSinceText}</span>
                    )}
                    {showCollab && (
                      <span className="cockpit__planCardSince">
                        {accountSinceText ? `Account since ${accountSinceText} · ` : ''}
                        You have access{isMasterMember ? ' · full access' : isMember ? ' as team member' : ''}
                      </span>
                    )}
                  </div>
                  <div className="cockpit__planCardStats">
                    <div className="cockpit__planCardStat">
                      <span className="cockpit__planCardStatValue">{planDetails.emailsPerDay}</span>
                      <span className="cockpit__planCardStatLabel">emails / day</span>
                    </div>
                    {planDetails.hasMembership && (
                      <div className="cockpit__planCardStat">
                        <span className="cockpit__planCardStatValue">{(planDetails.monthlyCredits || 0).toLocaleString()}</span>
                        <span className="cockpit__planCardStatLabel">credits / mo</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`cockpit__planCardFaces${showCollab ? ' cockpit__planCardFaces--pair' : ''}`}>
                  <div className="cockpit__planCardAvatar">
                    <img
                      src={ownerProfile.photo || '/img/user-three.svg'}
                      alt=""
                      className="cockpit__planCardAvatarImg"
                      onError={(e) => { e.currentTarget.src = '/img/user-three.svg'; }}
                    />
                    <span className="cockpit__planCardBadge cockpit__planCardRole--owner">Owner</span>
                  </div>
                  {showCollab && (
                    <div className="cockpit__planCardAvatar cockpit__planCardAvatar--you">
                      <img
                        src={selfProfile.photo || '/img/user-three.svg'}
                        alt=""
                        className="cockpit__planCardAvatarImg"
                        onError={(e) => { e.currentTarget.src = '/img/user-three.svg'; }}
                      />
                      <span className="cockpit__planCardBadge cockpit__planCardRole--guest">Access</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="cockpit__usageMeters">
              {creditMeter && creditMeter.extra > 0 && (
                <div className="cockpit__usageMeter">
                  <div className="cockpit__creditMeterTop">
                    <div className="cockpit__creditMeterLabel">
                      <span>Extra credits</span>
                    </div>
                    <span
                      className="cockpit__creditMeterValues"
                      title="These credits stay after the monthly reset."
                    >
                      <span className="cockpit__creditMeterUsed">{creditMeter.extra.toLocaleString()}</span>
                    </span>
                  </div>
                  <div className="cockpit__creditMeterTrack" aria-hidden>
                    <div className="cockpit__creditMeterBar cockpit__creditMeterBar--extra" style={{ width: '100%' }}>
                      <span className="cockpit__creditMeterPip" />
                    </div>
                  </div>
                  <div className="cockpit__creditMeterBottom">
                    <span className="cockpit__creditMeterRemaining" title="These credits stay after the monthly reset.">
                      Kept after the monthly reset
                    </span>
                  </div>
                </div>
              )}
              {creditMeter && (
                <div className="cockpit__usageMeter">
                  <div className="cockpit__creditMeterTop">
                    <div className="cockpit__creditMeterLabel">
                      <span>Credits</span>
                      {creditsAreShared && (
                        <span
                          className="cockpit__creditMeterSharedBadge"
                          title="This sub-instance shares the credit pool with the main account."
                        >
                          <i className="fa fa-link" aria-hidden /> Shared
                        </span>
                      )}
                    </div>
                    <span className="cockpit__creditMeterValues" title={creditMeter.remainingTitle}>
                      <span className="cockpit__creditMeterUsed">{creditMeter.remaining.toLocaleString()}</span>
                      {creditMeter.total > 0 && (
                        <>
                          <span className="cockpit__creditMeterSep">/</span>
                          <span className="cockpit__creditMeterTotal">{creditMeter.total.toLocaleString()}</span>
                        </>
                      )}
                    </span>
                  </div>
                  <div className="cockpit__creditMeterTrack" aria-hidden>
                    <div
                      className={`cockpit__creditMeterBar cockpit__creditMeterBar--${creditMeter.barTone}${creditMeter.remainingPct <= 0 ? ' cockpit__creditMeterBar--empty' : ''}`}
                      style={{ width: `${creditMeter.remainingPct}%` }}
                    >
                      <span className="cockpit__creditMeterPip" />
                    </div>
                  </div>
                  <div className="cockpit__creditMeterBottom">
                    <span className="cockpit__creditMeterRemaining" title={creditMeter.remainingTitle}>
                      {creditMeter.remainingLabel}
                    </span>
                    {creditMeter.refillText && (
                      <span
                        className="cockpit__creditMeterRefill"
                        title={creditMeter.refillTitle}
                      >
                        {creditMeter.refillText}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Daily email quota */}
              <div className="cockpit__usageMeter">
                <div className="cockpit__creditMeterTop">
                    <div className="cockpit__creditMeterLabel">
                      <span>Daily email quota</span>
                    </div>
                  <span className="cockpit__creditMeterValues">
                    <span className="cockpit__creditMeterUsed">{todaySent}</span>
                    <span className="cockpit__creditMeterSep">/</span>
                    <span className="cockpit__creditMeterTotal">{planDetails.emailsPerDay}</span>
                  </span>
                </div>
                <div className="cockpit__creditMeterTrack" aria-hidden>
                  <div
                    className={`cockpit__creditMeterBar cockpit__creditMeterBar--${quotaTone}${quotaPct <= 0 ? ' cockpit__creditMeterBar--empty' : ''}`}
                    style={{ width: `${quotaPct}%` }}
                  >
                    <span className="cockpit__creditMeterPip" />
                  </div>
                </div>
                <div className="cockpit__creditMeterBottom">
                  <span className="cockpit__creditMeterRemaining">
                    {Math.max(0, planDetails.emailsPerDay - todaySent)} left today
                  </span>
                </div>
              </div>
              </div>
            </div>

            {/* Activity: range metrics, setup counts and rank as one panel */}
            <section className="cockpit__activityStrip" aria-label="Email activity">
              <div className="cockpit__activityPanel">
                <div className="cockpit__activityHead">
                  <h2 className="cockpit__sectionTitle">Email activity</h2>
                  <div className="cockpit__rangePicker">
                    <select
                      className="cockpit__rangeSelect"
                      value={activityRange}
                      onChange={(e) => handleRangeChange(e.target.value)}
                      aria-label="Time range for email activity"
                    >
                      {ACTIVITY_RANGES.map((r) => (
                        <option key={r.id} value={r.id}>{r.label}</option>
                      ))}
                    </select>
                    {activityRange === 'custom' && (
                      <>
                        <input
                          type="date"
                          className="cockpit__rangeDate"
                          value={customStart}
                          max={customEnd || undefined}
                          onChange={(e) => setCustomStart(e.target.value)}
                          aria-label="Range start date"
                        />
                        <span className="cockpit__rangeDash" aria-hidden>–</span>
                        <input
                          type="date"
                          className="cockpit__rangeDate"
                          value={customEnd}
                          min={customStart || undefined}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          aria-label="Range end date"
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="cockpit__activityBody">
                  <div className="cockpit__activityMain">
                    <div className="cockpit__activityRow">
                      <div className="cockpit__analyticsCard">
                        <div className="cockpit__analyticsValueRow">
                          <span className="cockpit__analyticsValue">{rangeActivity.sent}</span>
                          {sentTrend !== 0 && (
                            <span className={`cockpit__trend cockpit__trend--${sentTrend > 0 ? 'up' : 'down'}`}>
                              <i className={`fa fa-arrow-${sentTrend > 0 ? 'up' : 'down'}`} aria-hidden />
                              {Math.abs(sentTrend)}%
                            </span>
                          )}
                        </div>
                        <span className="cockpit__analyticsLabel">Emails sent</span>
                        <span className="cockpit__analyticsHint">
                          {rangeLabel}{sentWindowHint ? ` · ${sentWindowHint}` : ''}
                        </span>
                      </div>
                      <div className="cockpit__analyticsCard cockpit__analyticsCard--accent">
                        <span className="cockpit__analyticsValue">{rangeActivity.positiveReplies}</span>
                        <span className="cockpit__analyticsLabel">Positive replies</span>
                        <span className="cockpit__analyticsHint">Score {POSITIVE_REPLY_MIN_SCORE}+ · {rangeLabel}</span>
                      </div>
                      <div className="cockpit__analyticsCard">
                        <span className="cockpit__analyticsValue">{replyRate}%</span>
                        <span className="cockpit__analyticsLabel">Reply rate</span>
                        <span className="cockpit__analyticsHint">{rangeActivity.replied} of {rangeActivity.sent}</span>
                      </div>
                      <div className="cockpit__analyticsCard">
                        <span className="cockpit__analyticsValue">{totalSent}</span>
                        <span className="cockpit__analyticsLabel">All time sent</span>
                        <span className="cockpit__analyticsHint">Lifetime</span>
                      </div>
                    </div>
                    <div className="cockpit__activityRow cockpit__activityRow--setup">
                      <div className="cockpit__quickStat" role="button" tabIndex={0} onClick={() => onOpenTab?.('email')}>
                        <span className="cockpit__quickStatValue">{data.emailAccountsCount}<span className="cockpit__quickStatMax">/{accountLimit}</span></span>
                        <span className="cockpit__quickStatLabel">Email accounts</span>
                      </div>
                      <div className="cockpit__quickStat" role="button" tabIndex={0} onClick={() => onOpenTab?.('topics')}>
                        <span className="cockpit__quickStatValue">{topicsCount + customTopicsCount}</span>
                        <span className="cockpit__quickStatLabel">Templates</span>
                      </div>
                      <div className="cockpit__quickStat" role="button" tabIndex={0} onClick={() => onOpenTab?.('agents')}>
                        <span className="cockpit__quickStatValue">{agentsCount}</span>
                        <span className="cockpit__quickStatLabel">Reply cases</span>
                      </div>
                      <div className="cockpit__quickStat" role="button" tabIndex={0} onClick={() => onOpenTab?.('leadSettings')}>
                        <span className="cockpit__quickStatValue">{flowsCount}</span>
                        <span className="cockpit__quickStatLabel">Lead flows</span>
                      </div>
                    </div>
                  </div>
                  <div className="cockpit__activityRank">
                    <div className={`cockpit__rankEmblem cockpit__rankEmblem--${rank.id}`}>
                      <div className="cockpit__rankEmblemShine" />
                      <button type="button" className="cockpit__rankInfoBtn" onClick={() => setShowRankInfo(true)} aria-label="How ranks work">
                        <i className="fa fa-info" aria-hidden />
                      </button>
                      <div className="cockpit__rankEmblemIcon">
                        <i className={`fa ${rank.icon}`} aria-hidden />
                      </div>
                      <div className="cockpit__rankEmblemLabel">{rank.label}</div>
                      <div className="cockpit__rankReplies">{positiveReplies} positive {positiveReplies === 1 ? 'reply' : 'replies'}</div>
                      {rank.nextAt !== Infinity && positiveReplies < rank.nextAt && (() => {
                        const nextIdx = RANK_TIERS.findIndex((r) => r.id === rank.id) + 1;
                        const nextTier = RANK_TIERS[nextIdx];
                        if (!nextTier) return null;
                        const progressInTier = positiveReplies - rank.min;
                        const tierRange = rank.nextAt - rank.min;
                        const pct = Math.round((progressInTier / tierRange) * 100);
                        return (
                          <div className="cockpit__rankXp">
                            <div className="cockpit__rankXpTrack">
                              <div className="cockpit__rankXpFill" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="cockpit__rankXpLabel">{rank.nextAt - positiveReplies} more to {nextTier.label}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Right column: Data Vault (long-term account brain) + Checklist */}
          <div className="cockpit__rightColumn">
          <CockpitDataVault
            mode="compact"
            userId={userId}
            currentUserId={currentUserId}
            canEdit={!!isOwner}
            onExpand={handleOpenVault}
          />
          {/* Checklist – right under percent bar */}
          <section className="cockpit__section cockpit__overview cockpit__checklistColumn">
            <div className="cockpit__checklistHead">
              <h2 className="cockpit__sectionTitle">Your checklist</h2>
              <div className="cockpit__checklistHeadMeta">
                <span className="cockpit__checklistProgressLabel">{doneCount}/{totalCount}</span>
                <span className="cockpit__checklistProgressPct">{totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0}%</span>
              </div>
            </div>
            <div className="cockpit__checklistProgress">
              <div className="cockpit__checklistProgressTrack">
                <div className="cockpit__checklistProgressFill" style={{ width: `${totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0}%` }} />
              </div>
            </div>
            <div className="cockpit__checklistGroups">
              {doneCount > 0 && (
                <div className="cockpit__checklistGroup">
                  <ul className="cockpit__checklist" aria-label="Completed items">
                    {breakdown.filter((b) => b.done).map((b) => (
                      <li key={b.id}>
                        <button type="button" className="cockpit__checkItem cockpit__checkItem--done" onClick={() => onOpenTab?.(b.tab)}>
                          <span className="cockpit__checkIcon cockpit__checkIcon--done"><i className="fa fa-check" aria-hidden /></span>
                          <span className="cockpit__checkLabel">{b.label}</span>
                          <i className="fa fa-chevron-right cockpit__checkArrow" aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {suggestions.length > 0 && (
                <div className="cockpit__checklistGroup">
                  <ul className="cockpit__checklist" aria-label="Open items">
                    {breakdown.filter((b) => !b.done).map((b) => (
                      <li key={b.id}>
                        <button type="button" className="cockpit__checkItem cockpit__checkItem--open" onClick={() => onOpenTab?.(b.tab)}>
                          <span className="cockpit__checkIcon cockpit__checkIcon--open"><i className="fa fa-circle" aria-hidden /></span>
                          <span className="cockpit__checkLabel">{b.label}</span>
                          <i className="fa fa-chevron-right cockpit__checkArrow" aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
          </div>
        </div>
      </div>

      {/* Features – Essentials */}
      <section className={`cockpit__featuresSection${basicsOpen ? ' is-open' : ' is-collapsed'}`}>
        <button
          type="button"
          className="cockpit__featuresSectionTitle"
          onClick={() => setBasicsOpen((open) => !open)}
          aria-expanded={basicsOpen}
        >
          <span className="cockpit__featuresSectionHead">
            <span className="cockpit__featuresSectionIcon" aria-hidden>
              <i className="fa fa-star" />
            </span>
            <span className="cockpit__featuresSectionCopy">
              <span className="cockpit__featuresSectionName">Basics</span>
              <span className="cockpit__featuresSectionHint">
                {basicsDone}/{basicsFeatures.length} done · {basicsOpen ? 'click to hide' : 'click to open'}
              </span>
            </span>
            <span className="cockpit__featuresSectionToggle">
              {basicsOpen ? 'Hide' : 'Show'}
              <i className={`fa fa-chevron-${basicsOpen ? 'up' : 'down'}`} aria-hidden />
            </span>
          </span>
          {!basicsOpen && <FeatureSectionPreview features={basicsFeatures} />}
        </button>
        {basicsOpen && (
        <div className="cockpit__featuresGrid">
          {FEATURE_CATALOG.filter((f) => f.tier === 'essential').map((feature) => {
            const { status, label } = getFeatureStatus(feature.id, breakdown, planTier, data);
            if (feature.id === 'team' && !isOwner) return null;
            return (
              <div key={feature.id} className={`cockpit__featureCard cockpit__featureCard--${status}`}>
                {status === 'done' && (
                  <span className="cockpit__featureCheck"><i className="fa fa-check" aria-hidden /></span>
                )}
                <div className="cockpit__featureBody">
                  <div className="cockpit__featureHead">
                    <span className={`cockpit__featureIcon cockpit__featureIcon--${status}`}><i className={feature.icon} aria-hidden /></span>
                    <div className="cockpit__featureHeadText">
                      <h3 className="cockpit__featureTitle">{feature.title}</h3>
                      <span className={`cockpit__featureBadge cockpit__featureBadge--${status}`}>
                        <i className={`fa ${status === 'done' ? 'fa-check-circle' : status === 'plan' ? 'fa-lock' : 'fa-circle'}`} aria-hidden /> {label}
                      </span>
                    </div>
                  </div>
                  <p className="cockpit__featureDesc">{feature.description}</p>
                  <div className="cockpit__featureActions">
                    {status === 'plan' ? (
                      <span className="cockpit__featureUpgrade"><i className="fa fa-arrow-up" aria-hidden /> Upgrade to {label}</span>
                    ) : (
                      <button type="button" className="cockpit__featureBtn cockpit__featureBtn--primary" onClick={() => onOpenTab?.(feature.tab)}>
                        {status === 'done' ? 'Adjust settings' : 'Set up now'} <i className="fa fa-chevron-right" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </section>

      {/* Features – Nice to have */}
      <section className={`cockpit__featuresSection${advancedOpen ? ' is-open' : ' is-collapsed'}`}>
        <button
          type="button"
          className="cockpit__featuresSectionTitle cockpit__featuresSectionTitle--extra"
          onClick={() => setAdvancedOpen((open) => !open)}
          aria-expanded={advancedOpen}
        >
          <span className="cockpit__featuresSectionHead">
            <span className="cockpit__featuresSectionIcon" aria-hidden>
              <i className="fa fa-puzzle-piece" />
            </span>
            <span className="cockpit__featuresSectionCopy">
              <span className="cockpit__featuresSectionName">Advanced</span>
              <span className="cockpit__featuresSectionHint">
                {advancedDone}/{advancedFeatures.length} done · {advancedOpen ? 'click to hide' : 'click to open'}
              </span>
            </span>
            <span className="cockpit__featuresSectionToggle">
              {advancedOpen ? 'Hide' : 'Show'}
              <i className={`fa fa-chevron-${advancedOpen ? 'up' : 'down'}`} aria-hidden />
            </span>
          </span>
          {!advancedOpen && <FeatureSectionPreview features={advancedFeatures} />}
        </button>
        {advancedOpen && (
        <div className="cockpit__featuresGrid">
          {FEATURE_CATALOG.filter((f) => f.tier === 'extra').map((feature) => {
            const { status, label } = getFeatureStatus(feature.id, breakdown, planTier, data);
            if (feature.id === 'team' && !isOwner) return null;
            return (
              <div key={feature.id} className={`cockpit__featureCard cockpit__featureCard--${status}`}>
                {status === 'done' && (
                  <span className="cockpit__featureCheck"><i className="fa fa-check" aria-hidden /></span>
                )}
                <div className="cockpit__featureBody">
                  <div className="cockpit__featureHead">
                    <span className={`cockpit__featureIcon cockpit__featureIcon--${status}`}><i className={feature.icon} aria-hidden /></span>
                    <div className="cockpit__featureHeadText">
                      <h3 className="cockpit__featureTitle">{feature.title}</h3>
                      <span className={`cockpit__featureBadge cockpit__featureBadge--${status}`}>
                        <i className={`fa ${status === 'done' ? 'fa-check-circle' : status === 'plan' ? 'fa-lock' : 'fa-circle'}`} aria-hidden /> {label}
                      </span>
                    </div>
                  </div>
                  <p className="cockpit__featureDesc">{feature.description}</p>
                  <div className="cockpit__featureActions">
                    {status === 'plan' ? (
                      <span className="cockpit__featureUpgrade"><i className="fa fa-arrow-up" aria-hidden /> Upgrade to {label}</span>
                    ) : (
                      <button type="button" className="cockpit__featureBtn cockpit__featureBtn--primary" onClick={() => onOpenTab?.(feature.tab)}>
                        {status === 'done' ? 'Adjust settings' : 'Set up now'} <i className="fa fa-chevron-right" aria-hidden />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </section>

      {showUpgradePlans && (
        <UpgradeAccountModal
          currentPlanId={planDetails.hasMembership ? planTier : null}
          onClose={() => setShowUpgradePlans(false)}
        />
      )}

      {/* Rank info modal – portalled to body. The cockpit keeps a transform
          from its enter animation, which would otherwise trap position:fixed. */}
      {showRankInfo && createPortal(
        <div
          className="cockpit__rankModal"
          onClick={() => setShowRankInfo(false)}
          role="presentation"
        >
          <div
            className="cockpit__rankModalCard"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cockpit-rank-modal-title"
          >
            <button type="button" className="cockpit__rankModalClose" onClick={() => setShowRankInfo(false)} aria-label="Close">
              <i className="fa fa-times" aria-hidden />
            </button>
            <h2 id="cockpit-rank-modal-title" className="cockpit__rankModalTitle">How Ranks Work</h2>
            <p className="cockpit__rankModalDesc">
              Your rank is based on all the <strong>positive replies</strong> (AI score {POSITIVE_REPLY_MIN_SCORE} or higher)
              you've received, not emails sent, and independent of the selected time range.
              Only genuinely interested responses from real leads count toward your next rank.
            </p>
            <div className="cockpit__rankModalTiers">
              {RANK_TIERS.map((tier) => {
                const isCurrent = tier.id === rank.id;
                return (
                  <div key={tier.id} className={`cockpit__rankModalTier ${isCurrent ? 'cockpit__rankModalTier--current' : ''}`}>
                    <div className={`cockpit__rankModalBadge cockpit__rankModalBadge--${tier.id}`}>
                      <i className={`fa ${tier.icon}`} aria-hidden />
                    </div>
                    <div className="cockpit__rankModalTierInfo">
                      <span className="cockpit__rankModalTierName">
                        {tier.label}
                        {isCurrent && <span className="cockpit__rankModalYou">← You</span>}
                      </span>
                      <span className="cockpit__rankModalTierReq">
                        {tier.min === 0 ? '0' : `${tier.min.toLocaleString()}+`} positive replies
                      </span>
                      <span className="cockpit__rankModalTierDesc">{tier.description}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="cockpit__rankModalStats">
              <span>Your positive replies: <strong>{positiveReplies}</strong></span>
              {rank.nextAt !== Infinity && (
                <span>{(rank.nextAt - positiveReplies).toLocaleString()} more to reach {RANK_TIERS[RANK_TIERS.findIndex((r) => r.id === rank.id) + 1]?.label}</span>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Floating potential bubble – replaces the hero once setup is ≥80% done.
          Portalled to the body because the cockpit keeps a transform from its
          entry animation, which would trap a position:fixed child. */}
      {heroCompact && createPortal(
        <div className="cockpit__potential">
          {potentialOpen && (
            <>
              <div className="cockpit__potentialBackdrop" onClick={() => setPotentialOpen(false)} />
              <div className="cockpit__potentialPanel" role="dialog" aria-label="SalesGod potential">
                <div className="cockpit__potentialHead">
                  <span className="cockpit__potentialTitle">
                    <i className="fa fa-bolt" aria-hidden /> SalesGod Potential
                  </span>
                  <button
                    type="button"
                    className="cockpit__potentialClose"
                    onClick={() => setPotentialOpen(false)}
                    aria-label="Close"
                  >
                    <i className="fa fa-times" aria-hidden />
                  </button>
                </div>
                <div className="cockpit__potentialScore">
                  <span className="cockpit__potentialScoreValue">
                    {percent}<span className="cockpit__potentialScoreUnit">%</span>
                  </span>
                  <div className="cockpit__potentialScoreBody">
                    <span className="cockpit__potentialHeadline">{heroIntel.headline}</span>
                    <span className="cockpit__potentialMeta">
                      <span className="cockpit__heroMetaHighlight">{doneCount}</span> of {totalCount} complete
                    </span>
                  </div>
                </div>
                <div className="cockpit__progressTrack cockpit__progressTrack--hero cockpit__progressTrack--compact">
                  <div
                    className="cockpit__progressBar cockpit__progressBar--hero"
                    style={{ width: `${percent}%`, background: percent >= 100 ? '#34c759' : undefined }}
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                {heroIntel.insight && <p className="cockpit__potentialInsight">{heroIntel.insight}</p>}
                {suggestions.length > 0 ? (
                  <>
                    <span className="cockpit__potentialNextLabel">Your next step</span>
                    <button
                      type="button"
                      className="cockpit__potentialNext"
                      onClick={() => { setPotentialOpen(false); onOpenTab?.(suggestions[0].tab); }}
                    >
                      <i className={suggestions[0].icon} aria-hidden />
                      <span className="cockpit__potentialNextText">{suggestions[0].text}</span>
                      <i className="fa fa-arrow-right" aria-hidden />
                    </button>
                    {suggestions.length > 1 && (
                      <ul className="cockpit__potentialRest">
                        {suggestions.slice(1, 4).map((s) => (
                          <li key={s.id}>
                            <button
                              type="button"
                              className="cockpit__potentialRestItem"
                              onClick={() => { setPotentialOpen(false); onOpenTab?.(s.tab); }}
                            >
                              <i className={s.icon} aria-hidden />
                              <span className="cockpit__potentialRestText">{s.text}</span>
                              <i className="fa fa-chevron-right" aria-hidden />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="cockpit__potentialAllDone">
                    <i className="fa fa-check-circle" aria-hidden /> Everything is set up. Nothing left to do.
                  </p>
                )}
              </div>
            </>
          )}
          <button
            type="button"
            className={`cockpit__potentialBubble ${percent >= 100 ? 'cockpit__potentialBubble--full' : ''}`}
            style={{ '--cockpit-potential-pct': percent }}
            onClick={() => setPotentialOpen((open) => !open)}
            aria-expanded={potentialOpen}
            aria-label={`SalesGod potential ${percent}%. Show details`}
            title={`${heroIntel.headline} · ${percent}% potential`}
          >
            <span className="cockpit__potentialBubbleValue">
              {percent}<span className="cockpit__potentialBubbleUnit">%</span>
            </span>
            {suggestions.length > 0 && <span className="cockpit__potentialBubbleDot" />}
          </button>
        </div>,
        document.body
      )}

    </div>
  );
}
